/**
 * Canonical employee certification vocabulary — the frontend's single copy.
 *
 * Mirrors the API's `src/constants/certification-types.ts`, which is the real
 * source of truth: `employee_certifications.certification_type` is
 * varchar(50) with no CHECK constraint, so that list — not the database — is
 * what enforces the vocabulary, and every API write path validates against it.
 * A value that drifts out of sync here is a 400 the worker meets in the field.
 *
 * Do not rename or reorder the original ten: existing rows store those exact
 * strings. `other` stays last; it is the escape hatch and the bottom of every
 * dropdown.
 *
 * Before this file was consolidated, five screens carried their own inline
 * `CERT_TYPE_LABELS` object. All five were stuck at the original ten values,
 * so the nine types added for cert scanning rendered as raw snake_case on
 * every screen that showed a worker their own tickets.
 */

/** The 20 canonical values, in display order. */
export const CERTIFICATION_TYPES = [
  // ── original ten, exact strings, exact order — do not rename or reorder ──
  'H2S_alive',
  'CSTS',
  'standard_first_aid',
  'ground_disturbance',
  'WHMIS',
  'OSHA_10',
  'OSHA_30',
  'H2S_awareness',
  'first_aid_CPR',
  'drivers_licence',

  // ── added for AI cert scanning (API PR #294) ────────────────────────────
  'common_safety_orientation',
  'fall_protection',
  'confined_space_entry',
  'TDG',
  'fire_watch',
  'detection_control_flammable_substances',
  'respiratory_fit_test',
  'forklift',
  'aerial_work_platform',

  // ── escape hatch, always last; requires custom_type ─────────────────────
  'other',
] as const;

export type CertificationType = (typeof CERTIFICATION_TYPES)[number];

/**
 * Human labels for the canonical vocabulary. Typed as a total Record, so a
 * value added above without a label here is a compile error rather than a
 * screen rendering the raw snake_case string.
 */
export const CERT_TYPE_LABELS: Record<CertificationType, string> = {
  H2S_alive: 'H2S Alive',
  CSTS: 'CSTS',
  standard_first_aid: 'Standard First Aid',
  ground_disturbance: 'Ground Disturbance',
  WHMIS: 'WHMIS',
  OSHA_10: 'OSHA 10',
  OSHA_30: 'OSHA 30',
  H2S_awareness: 'H2S Awareness',
  first_aid_CPR: 'First Aid / CPR',
  drivers_licence: "Driver's Licence",
  common_safety_orientation: 'Common Safety Orientation (CSO)',
  fall_protection: 'Fall Protection',
  confined_space_entry: 'Confined Space Entry',
  TDG: 'Transportation of Dangerous Goods (TDG)',
  fire_watch: 'Fire Watch',
  detection_control_flammable_substances: 'Detection & Control of Flammable Substances',
  respiratory_fit_test: 'Respiratory Fit Test',
  forklift: 'Forklift',
  aerial_work_platform: 'Aerial Work Platform',
  other: 'Other',
};

/**
 * Display-only labels for values that are NOT in the canonical vocabulary but
 * may still sit in stored rows.
 *
 * `air_brake` was in DriverSafetyScreen's inline label map and has never been
 * an accepted `certification_type` — the API's write paths reject it now. Rows
 * predating that enforcement still render, and "Air Brake Endorsement" is a
 * better answer than "air_brake". These are deliberately kept OUT of
 * `CERTIFICATION_TYPES` so they never appear in a picklist: a worker choosing
 * one would get a 400 on save.
 */
const LEGACY_CERT_TYPE_LABELS: Record<string, string> = {
  air_brake: 'Air Brake Endorsement',
};

/**
 * Label for a stored value. Falls back to the raw value so a row written
 * before a type existed still renders something rather than blank — but
 * every value the app can currently write has a real label above.
 */
export function certTypeLabel(value: string | null | undefined): string {
  if (!value) return '';
  return (
    CERT_TYPE_LABELS[value as CertificationType] ?? LEGACY_CERT_TYPE_LABELS[value] ?? value
  );
}

/**
 * Loose-keyed label lookup, canonical values plus the legacy display-only ones.
 *
 * `CERT_TYPE_LABELS` is a total Record over the union, so indexing it with an
 * arbitrary string off an API payload does not typecheck. Screens that read a
 * stored `certification_type` use this instead, which keeps their existing
 * `MAP[a] || MAP[b] || cert.name` fallback chains working unchanged — an
 * unknown key still misses, so those call sites still reach their own
 * fallbacks rather than being handed a raw snake_case string.
 */
export const CERT_TYPE_LABEL_MAP: Record<string, string> = {
  ...CERT_TYPE_LABELS,
  ...LEGACY_CERT_TYPE_LABELS,
};

export interface CertType {
  value: string;
  label: string;
}

/** The picklist shape. Canonical values only — never the legacy labels. */
export const CERT_TYPES: CertType[] = CERTIFICATION_TYPES.map((value) => ({
  value,
  label: CERT_TYPE_LABELS[value],
}));
